import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChefHat, ClipboardList, Settings, Utensils, Wallet } from "lucide-react";

const ROLE_PRESETS = [
    {
        key: "garson",
        label: "Garson",
        icon: ClipboardList,
        username: "garson",
        description: "Masadan sipariş alın",
    },
    {
        key: "mutfak",
        label: "Mutfak",
        icon: ChefHat,
        username: "mutfak",
        description: "Siparişleri hazırlayın",
    },
    {
        key: "kasa",
        label: "Kasa",
        icon: Wallet,
        username: "kasa",
        description: "Ödeme alın ve kapatın",
    },
    {
        key: "admin",
        label: "Yönetici",
        icon: Settings,
        username: "admin",
        description: "Menü ve sistem yönetimi",
    },
];

export default function LoginPage() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [activeRole, setActiveRole] = useState("garson");
    const [username, setUsername] = useState("garson");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    if (user) {
        const map = { garson: "/garson", mutfak: "/mutfak", kasa: "/kasa", admin: "/admin" };
        return <Navigate to={map[user.role] || "/garson"} replace />;
    }

    const handleRolePick = (preset) => {
        setActiveRole(preset.key);
        setUsername(preset.username);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!username || !password) {
            toast.error("Kullanıcı adı ve şifre gerekli");
            return;
        }
        setSubmitting(true);
        const res = await login(username.trim(), password);
        setSubmitting(false);
        if (!res.ok) {
            toast.error(res.error || "Giriş başarısız");
            return;
        }
        toast.success(`Hoş geldin ${res.user.name}`);
        const map = { garson: "/garson", mutfak: "/mutfak", kasa: "/kasa", admin: "/admin" };
        navigate(map[res.user.role] || "/garson");
    };

    return (
        <div className="min-h-screen flex flex-col md:flex-row">
            {/* Left visual panel */}
            <div className="hidden md:flex md:w-1/2 relative bg-[#2C302E] overflow-hidden">
                <img
                    src="https://images.pexels.com/photos/6213723/pexels-photo-6213723.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940"
                    alt="restoran"
                    className="absolute inset-0 w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#2C302E]/85 via-[#2C302E]/50 to-transparent" />
                <div className="relative z-10 p-12 flex flex-col justify-between text-white">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#D96C4A] flex items-center justify-center">
                            <Utensils className="w-5 h-5" />
                        </div>
                        <span className="font-display text-xl font-semibold">Sofra</span>
                    </div>
                    <div className="space-y-3 max-w-md">
                        <h1 className="font-display text-4xl lg:text-5xl font-semibold leading-[1.05]">
                            Kağıt yok.<br />
                            Karışıklık yok.
                        </h1>
                        <p className="text-base text-white/80 leading-relaxed">
                            Garsonun aldığı sipariş, anında mutfağa düşer. Hazırlanan her yemek tek dokunuşla işaretlenir.
                        </p>
                    </div>
                </div>
            </div>

            {/* Right form panel */}
            <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-[#FAF9F6]">
                <div className="w-full max-w-md fade-up">
                    <div className="md:hidden mb-6 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-[#D96C4A] text-white flex items-center justify-center">
                            <Utensils className="w-5 h-5" />
                        </div>
                        <span className="font-display text-xl font-semibold text-[#2C302E]">Sofra</span>
                    </div>

                    <div className="mb-8">
                        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#59605B] mb-2">
                            Giriş Yap
                        </div>
                        <h2 className="font-display text-3xl font-semibold text-[#2C302E]">
                            Hoş geldiniz
                        </h2>
                        <p className="text-sm text-[#59605B] mt-2">
                            Rolünüzü seçin ve şifrenizle giriş yapın.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                        {ROLE_PRESETS.map((p) => {
                            const Icon = p.icon;
                            const active = activeRole === p.key;
                            return (
                                <button
                                    key={p.key}
                                    type="button"
                                    onClick={() => handleRolePick(p)}
                                    data-testid={`role-${p.key}`}
                                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border transition-all text-sm font-medium ${
                                        active
                                            ? "bg-[#D96C4A] text-white border-[#D96C4A] -translate-y-0.5 shadow-md"
                                            : "bg-white text-[#2C302E] border-[#E5E3D8] hover:-translate-y-0.5 hover:border-[#D96C4A]/40"
                                    }`}
                                >
                                    <Icon className="w-5 h-5" />
                                    {p.label}
                                </button>
                            );
                        })}
                    </div>

                    <form onSubmit={onSubmit} className="space-y-4">
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-[#59605B] block mb-2">
                                Kullanıcı adı
                            </label>
                            <input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                data-testid="login-username"
                                autoComplete="username"
                                className="w-full bg-white border border-[#E5E3D8] rounded-lg px-4 py-3 text-[#2C302E] focus:outline-none focus:border-[#D96C4A] focus:ring-1 focus:ring-[#D96C4A] transition-all"
                                placeholder="garson"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-[#59605B] block mb-2">
                                Şifre
                            </label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="login-password"
                                autoComplete="current-password"
                                className="w-full bg-white border border-[#E5E3D8] rounded-lg px-4 py-3 text-[#2C302E] focus:outline-none focus:border-[#D96C4A] focus:ring-1 focus:ring-[#D96C4A] transition-all"
                                placeholder="••••••••"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="login-submit"
                            className="w-full bg-[#D96C4A] text-white font-medium px-6 py-3 rounded-lg hover:bg-[#C05A3B] active:scale-[0.99] transition-all disabled:opacity-60"
                        >
                            {submitting ? "Giriş yapılıyor..." : "Giriş Yap"}
                        </button>
                    </form>

                    <div className="mt-8 p-4 bg-[#F3F1EB] rounded-lg text-xs text-[#59605B] leading-relaxed">
                        <div className="font-semibold text-[#2C302E] mb-1">Demo hesaplar</div>
                        garson / garson123 · mutfak / mutfak123 · kasa / kasa123 · admin / admin123
                    </div>
                </div>
            </div>
        </div>
    );
}
